import React, { useContext, useState } from "react";
import { PatientContext } from "../../lib/main-context";
import { useTranslation } from "../../i18n/TranslationProvider";

import { Row, Col } from "react-bootstrap";
import Nav from "react-bootstrap/Nav";
import Tab from "react-bootstrap/Tab";
import styled from "styled-components";

import { removeTrackedFactors, postPausedList, replaceWorstFactors } from "../../api/TBIRequests";
import SymptomReplacePicker from "./SymptomReplacePicker";

const Container = styled.div`
  margin: 10px;
  h6 { text-align: center; }
  ul { font-weight: normal; font-size: 15px; margin: 10px; }
`;

const UITrackingList = ({
  symptoms, pauseList, setPauseList, pausedFactorObj, setPausedFactorObj,
  setTopSymptoms, worstSymptoms, setWorstSymptoms, title, user,
}) => {
  const { patient } = useContext(PatientContext);
  const { t } = useTranslation();

  const [replaceModal, setReplaceModal] = useState({ show: false, oldFactor: null });

  const tSymptom = (category, factor) => {
    const key = `symptom.${category}.${(factor || "").toLowerCase()
      .replace(/ /g,"_").replace(/\//g,"_").replace(/,/g,"")
      .replace(/:/g,"").replace(/'/g,"").replace(/[()]/g,"").replace(/-/g,"_")
      .substring(0, 60)}`;
    const translated = t(key);
    return translated !== key ? translated : factor;
  };

  const addPauseFactor = (newone) => {
    let newParsedFactors = [];
    if (pauseList) {
      if (!pauseList.includes(newone)) {
        newParsedFactors = [...pauseList, newone];
        const objWithFactor = symptoms?.find((symptom) => symptom.factor === newone);
        const newPausedFactorObj = [...pausedFactorObj, objWithFactor];
        setPausedFactorObj(newPausedFactorObj);
      } else {
        newParsedFactors = [...pauseList];
      }
    } else {
      newParsedFactors = [newone];
    }
    setPauseList(newParsedFactors);
    postPausedList({
      patientId: patient.patientId,
      therapistId: user.userType === "Therapist" ? user.userId : null,
      data: [[newone]],
    }).catch(() => console.log("postPausedList error"));
  };

  const removeTracking = (s) => {
    let newTopSymptoms = symptoms.filter((sy) => sy.factor !== s);
    setTopSymptoms(newTopSymptoms);
    removeTrackedFactors({
      patientId: patient.patientId,
      therapistId: user?.userType === "Therapist" ? user.userId : null,
      data: [s],
    }).catch(() => console.log("removeTrackedFactors error"));
  };

  const isTop3 = (factor) =>
    !!worstSymptoms?.some((s) => s.factor === factor);

  // Full pause: removes from both the worst-3 list (if applicable) and the
  // all-symptoms list.
  const pauseFully = (filteredSymptom) => {
    addPauseFactor(filteredSymptom.factor);
    removeTracking(filteredSymptom.factor);
  };

  const handlePauseClick = (filteredSymptom) => {
    if (isTop3(filteredSymptom.factor)) {
      setReplaceModal({ show: true, oldFactor: filteredSymptom.factor });
    } else {
      pauseFully(filteredSymptom);
    }
  };

  const handleReplaceConfirm = (chosenReplacement) => {
    const oldFactor = replaceModal.oldFactor;
    replaceWorstFactors({
      patientId: patient.patientId,
      data: { pairs: [{ oldFactor, newSymptom: chosenReplacement }] },
    })
      .then(() => {
        setWorstSymptoms((prev) =>
          prev.map((s) => (s.factor === oldFactor ? chosenReplacement : s))
        );
        pauseFully({ factor: oldFactor });
      })
      .catch(() => console.log("replaceWorstFactors error"))
      .finally(() => setReplaceModal({ show: false, oldFactor: null }));
  };

  const messages = {
    cognitive: `${t("addsymptoms.category.cognitive")}: `,
    sleep: `${t("addsymptoms.category.sleep")}: `,
    physical: `${t("addsymptoms.category.physical")}: `,
    emotional: `${t("addsymptoms.category.emotional")}: `,
    speech: `${t("addsymptoms.category.speech")}: `,
    vision: `${t("addsymptoms.category.vision")}: `,
  };

  const tabs = {
    cognitive: t("addsymptoms.category.cognitive"),
    sleep: t("addsymptoms.category.sleep"),
    physical: t("addsymptoms.category.physical"),
    emotional: t("addsymptoms.category.emotional"),
    speech: t("addsymptoms.category.speech"),
    vision: t("addsymptoms.category.vision"),
  };

  const uniqueSymptoms = [];
  const factors = new Set();
  for (const symptom of symptoms) {
    if (!symptom?.factor) continue; // guard against malformed rows (null/missing factor)
    if (!factors.has(symptom.factor)) {
      factors.add(symptom.factor);
      uniqueSymptoms.push(symptom);
    }
  }

  const uniqueSubcategories = new Set();
  uniqueSymptoms.forEach((symptom) => uniqueSubcategories.add(symptom.subcategory));
  const uniqueSubcategoryArray = Array.from(uniqueSubcategories);

  const colors = {
    cognitive: "#edebf3", sleep: "#ffdadb", physical: "#ffdcaf",
    emotional: "#fff3c4", speech: "#cef3c4", vision: "#c1fdf7",
    wellness: "#dab7ec", travel: "#ffdadb", "screen time": "#ffdcaf",
    environmental: "#fff3c4", Dietary: "#cef3c4", situational: "#c1fdf7",
    Alternative: "#dab7ec", Applied: "#ffdadb", Chiropractic: "#ffdcaf",
    Vision: "#fff3c4", "Mental Health": "#c1fdf7", Other: "#f4f3ee",
  };

  // Candidates to fill in for a paused top-3 symptom: any currently tracked
  // symptom that isn't already in the top-3.
  const replaceCandidates = uniqueSymptoms.filter(
    (s) => !worstSymptoms?.some((w) => w.factor === s.factor)
  );

  return (
    <Container>
      <h6>{title}</h6>
      <Tab.Container
        id="left-tabs-example"
        defaultActiveKey={uniqueSubcategoryArray.length > 0 ? uniqueSubcategoryArray[0] : undefined}
      >
        <Row style={{ marginTop: "0.5rem", fontWeight: "normal", fontSize: "15px" }}>
          <Col sm={3} style={{ paddingRight: "0px" }}>
            <Nav fill justify variant="tabs" className="flex-column">
              {uniqueSubcategoryArray.map((subcategory, i) => (
                <Nav.Item key={i}>
                  <Nav.Link
                    className="navLink"
                    eventKey={subcategory}
                    style={{ background: `${colors[subcategory]}`, border: "1px solid #106995" }}
                  >
                    {" "}{tabs[subcategory]}
                  </Nav.Link>
                </Nav.Item>
              ))}
            </Nav>
          </Col>
          <Col sm={7} style={{ marginLeft: "1rem", paddingRight: "0px" }}>
            <Tab.Content>
              {uniqueSubcategoryArray.map((subcategory, i) => (
                <Tab.Pane eventKey={subcategory} key={i}>
                  <h6 className="d-inline">{messages[subcategory]}</h6>
                  <ul className="list-disc pl-1">
                    {uniqueSymptoms
                      .filter((symptom) => symptom.subcategory === subcategory)
                      .map((filteredSymptom, j) => (
                        <li key={j} className="mb-2">
                          {tSymptom(subcategory, filteredSymptom.factor)}
                          {isTop3(filteredSymptom.factor) && (
                            <span
                              className="ml-1"
                              style={{ fontSize: "12px", fontWeight: 600, color: "#106995" }}
                            >
                              {" "}({t("uitrackinglist.top3_badge")})
                            </span>
                          )}{" "}
                          <button
                            onClick={() => handlePauseClick(filteredSymptom)}
                            className="px-2 py-1 rounded bg-red-500 text-white hover:bg-red-900 transition"
                          >
                            {t("uitrackinglist.pause")}
                          </button>
                        </li>
                      ))}
                  </ul>
                </Tab.Pane>
              ))}
            </Tab.Content>
          </Col>
        </Row>
      </Tab.Container>

      <SymptomReplacePicker
        show={replaceModal.show}
        title={t("uitrackinglist.choose_replacement")}
        symptoms={replaceCandidates}
        onConfirm={handleReplaceConfirm}
        onCancel={() => setReplaceModal({ show: false, oldFactor: null })}
      />
    </Container>
  );
};

export default UITrackingList;